/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { Observable, Subject, Subscription } from 'rxjs';
import { equals, EqualsOptions } from '../helper/equals.helper';
import { CoreErrorKey } from '../key/core-error.key';
import { ApplicationError } from '../model/application-error.model';
import { Change } from '../model/change.model';
import { ChangedEvent } from '../model/changed-event.model';
import { CompareMode, CompareModes } from '../model/compare-mode.model';
import { ObserveMode, ObserveModes } from '../model/observe-mode.model';
import { MapIterator } from './map-iterator.util';
import { ObjectAdapter } from './object-adapter.util';

export interface ObservableCollectionEntry<K extends string | number, V> {
  key: K;
  value: V;
  changedSubscription?: Subscription;
}

export interface ObservableCollectionOptions {
  observeMode?: ObserveMode;
  compareMode?: CompareMode;
}

export interface ChangeOptions {
  exclusive?: boolean;
  force?: boolean;
  silent?: boolean;
}

export abstract class ObservableCollection<K extends string | number, V> implements Iterable<V> {
  private changedEventSubject = new Subject<ChangedEvent>();
  private observableCollectionOptions: ObservableCollectionOptions;

  constructor(options?: ObservableCollectionOptions) {
    this.observableCollectionOptions = {
      observeMode: options && options.observeMode || ObserveModes.LOCAL,
      compareMode: options && options.compareMode || CompareModes.STRICT
    };
  }

  get changedEvent(): Observable<ChangedEvent> {
    return this.changedEventSubject.asObservable();
  }

  get observeMode(): ObserveMode {
    return this.observableCollectionOptions.observeMode;
  }

  set observeMode(value: ObserveMode) {
    if (value === this.observableCollectionOptions.observeMode) {
      return;
    }
    const originalValue = this.observableCollectionOptions.observeMode;
    switch (value) {
      case ObserveModes.LOCAL:
        for (const entry of this.internalEntries()) {
          this.unsubscribeEntryValueChangedEvent(entry);
        }
        break;
      case ObserveModes.FLAT:
        if (originalValue === ObserveModes.LOCAL) {
          for (const entry of this.internalEntries()) {
            if (entry.value instanceof ObservableCollection) {
              this.subscribeEntryValueChangedEvent(entry);
            }
          }
        }
        break;
      case ObserveModes.DEEP:
        if (originalValue === ObserveModes.LOCAL) {
          for (const entry of this.internalEntries()) {
            if (entry.value instanceof ObservableCollection) {
              this.subscribeEntryValueChangedEvent(entry);
              entry.value.observeMode = ObserveModes.DEEP;
            }
          }
        } else if (originalValue === ObserveModes.FLAT) {
          for (const entry of this.internalEntries()) {
            if (entry.value instanceof ObservableCollection) {
              entry.value.observeMode = ObserveModes.DEEP;
            }
          }
        }
        break;
      default:
        throw new ApplicationError({
          key: CoreErrorKey.OBSERVE_MODE_INVALID,
          arguments: { observeMode: value },
          message: `Observe mode '${value}' invalid`
        });
    }
    this.observableCollectionOptions.observeMode = value;
  }

  get compareMode(): CompareMode {
    return this.observableCollectionOptions.compareMode;
  }

  set compareMode(value: CompareMode) {
    if (value !== CompareModes.STRICT && value !== CompareModes.EQUAL) {
      throw new ApplicationError({
        key: CoreErrorKey.COMPARE_MODE_INVALID,
        arguments: { compareMode: value },
        message: `Compare mode '${value}' invalid`
      });
    }
    this.observableCollectionOptions.compareMode = value;
  }

  abstract get size(): number;

  has(key: K) {
    return this.internalHasEntry(key);
  }

  get(key: K, defaultValue?: V): V {
    const entry = this.internalGetEntry(key);
    return (entry && entry.value !== undefined) ? entry.value : defaultValue;
  }

  set(key: K, value: V, options?: ChangeOptions): void {
    this.setValues({ [key]: value }, options);
  }

  remove(key: K, silent: boolean = false): boolean {
    const originalEntry = this.internalGetEntry(key);
    if (!this.internalRemoveEntry(key)) {
      return false;
    }
    if (!originalEntry) {
      return true;
    }
    this.unsubscribeEntryValueChangedEvent(originalEntry);
    if (originalEntry.value !== undefined && !silent) {
      const change: Change<V> = new Change({ originalValue: originalEntry.value, currentValue: undefined });
      this.fireChangedEvent(new ChangedEvent({ source: this, changes: { [key]: change } }));
    }
    return true;
  }

  clear(silent: boolean = false): void {
    this.setValues({}, { exclusive: true, silent });
  }

  setValues(values: object, options?: ChangeOptions): { [key: string]: Change<V> } {
    const exclusive = options && options.exclusive || false;
    const force = options && options.force || false;
    const changes = this.internalChangeValues(values || {}, exclusive, force);
    if (changes) {
      this.onChangedValues(changes, options);
    }
    return changes;
  }

  [Symbol.iterator](): IterableIterator<V> {
    return this.values();
  }

  keys(): IterableIterator<K> {
    return new MapIterator(
      this.internalEntries(), (entry, index) => {
        // use index as key for sparse arrays with missing entries
        return entry ? entry.key : index as K;
      }
    );
  }

  values(): IterableIterator<V> {
    return new MapIterator(
      this.internalEntries(), (entry) => {
        return entry ? entry.value : undefined;
      }
    );
  }

  entries(): IterableIterator<[K, V]> {
    return new MapIterator(
      this.internalEntries(), (entry, index) => {
        // use index as key for sparse arrays with missing entries
        return entry ? [entry.key, entry.value] : [index as K, undefined];
      }
    );
  }

  equals(other: any, options?: EqualsOptions): boolean {
    return equals(this, other, options);
    // if (this === other) {
    //   return true;
    // }
    // if (!other || other.constructor !== this.constructor || other.size !== this.size) {
    //   return false;
    // }
    // for (const entry of this.internalEntries()) {
    //   const thisValue: V = entry.value;
    //   const otherValue: V = other.get(entry.key);
    //   if (!equals(thisValue, otherValue)) {
    //     return false;
    //   }
    // }
    // return true;
  }

  forEach(callback: (value: V, key?: K) => void) {
    for (const entry of this.internalEntries()) {
      callback(entry.value, entry.key);
    }
  }

  toArray(): V[] {
    return Array.from(this.values());
  }

  toObjectAdapter(): ObjectAdapter {
    const source = this;
    return new ObjectAdapter(
      source,
      () => source.keys(),
      () => source.values(),
      () => source.entries(),
      (key: K) => source.has(key),
      (key: K) => source.get(key),
      (key: K, value: V) => source.set(key, value),
      (key: K) => source.remove(key),
      () => source.size
    );
  }

  keyOf(value: V, compareMode: CompareMode = CompareModes.STRICT): K {
    switch (compareMode) {
      case CompareModes.STRICT:
        return this.firstKey((entryValue: V) => value === entryValue);
      case CompareModes.EQUAL:
        return this.firstKey((entryValue: V) => equals(entryValue, value));
    }
    throw new ApplicationError({
      key: CoreErrorKey.COMPARE_MODE_INVALID,
      arguments: { compareMode },
      message: `Compare mode '${compareMode}' invalid`
    });
  }

  firstKey(predicate: (value: V, key?: K, index?: number) => boolean = (() => true)): K {
    const firstEntry = this.firstEntry(predicate);
    return firstEntry && firstEntry[0];
  }

  firstValue(predicate: (value: V, key?: K, index?: number) => boolean = (() => true)): V {
    const firstEntry = this.firstEntry(predicate);
    return firstEntry && firstEntry[1];
  }

  firstEntry(predicate: (value: V, key?: K, index?: number) => boolean = (() => true)): [K, V] {
    let index = 0;
    for (const entry of this.internalEntries()) {
      if (predicate(entry.value, entry.key, index)) {
        return [entry.key, entry.value];
      }
      index++;
    }
    return undefined;
  }

  findKeys(predicate: (value: V, key?: K, index?: number) => boolean): Array<K> {
    const values: Array<K> = [];
    let index = 0;
    for (const entry of this.internalEntries()) {
      if (predicate(entry.value, entry.key, index)) {
        values.push(entry.key);
      }
      index++;
    }
    return values;
  }

  findValues(predicate: (value: V, key?: K, index?: number) => boolean): Array<V> {
    const values: Array<V> = [];
    let index = 0;
    for (const entry of this.internalEntries()) {
      if (predicate(entry.value, entry.key, index)) {
        values.push(entry.value);
      }
      index++;
    }
    return values;
  }

  findEntries(predicate: (value: V, key?: K, index?: number) => boolean): Array<[K, V]> {
    const entries: Array<[K, V]> = [];
    let index = 0;
    for (const entry of this.internalEntries()) {
      if (predicate(entry.value, entry.key, index)) {
        entries.push([entry.key, entry.value]);
      }
      index++;
    }
    return entries;
  }

  protected internalCreateEntry(key: K, value: V): ObservableCollectionEntry<K, V> {
    return { key, value };
  }

  protected abstract internalHasEntry(key: K): boolean;

  protected abstract internalGetEntry(key: K): ObservableCollectionEntry<K, V>;

  protected abstract internalSetEntry(entry: ObservableCollectionEntry<K, V>): void;

  protected abstract internalRemoveEntry(key: K): boolean;

  protected abstract internalEntries(): IterableIterator<ObservableCollectionEntry<K, V>>;

  private internalChangeValues(
    values: object, exclusive: boolean, force: boolean
  ): { [key: string]: Change<V> } {
    if (!values) {
      values = {};
    }
    const valuesReader = ObjectAdapter.from(values);
    const changes: { [key: string]: Change<V> } = {};
    let hasChanges = false;
    if (exclusive) {
      for (const entry of this.internalEntries()) {
        if (entry && !valuesReader.has(entry.key)) {
          const modification = this.internalChangeValue(entry.key as K, undefined, force);
          if (modification) {
            changes[entry.key] = modification;
            hasChanges = true;
          }
        }
      }
    }
    for (const [key, value] of valuesReader.entries()) {
      const modification = this.internalChangeValue(key as K, value, force);
      if (modification) {
        changes[key] = modification;
        hasChanges = true;
      }
    }
    return hasChanges ? changes : null;
  }

  private internalChangeValue(key: K, value: V, force: boolean): Change<V> {
    const originalEntry = this.internalGetEntry(key);
    let originalValue: V;
    if (originalEntry) {
      originalValue = originalEntry.value;
      if (originalValue === value) {
        return null;
      }
      if (!force) {
        if (this.compareMode === CompareModes.EQUAL) {
          if (equals(originalValue, value)) {
            return null;
          }
        }
        if (Number.isNaN(value as any) && Number.isNaN(originalValue as any)) {
          return null;
        }
      }
      this.unsubscribeEntryValueChangedEvent(originalEntry);
    } else {
      originalValue = undefined;
      if (originalValue === value) {
        return null;
      }
    }
    const entry: ObservableCollectionEntry<K, V> = this.internalCreateEntry(key, value);
    if (value instanceof ObservableCollection) {
      switch (this.observeMode) {
        case ObserveModes.FLAT:
          this.subscribeEntryValueChangedEvent(entry);
          break;
        case ObserveModes.DEEP:
          this.subscribeEntryValueChangedEvent(entry);
          value.observeMode = ObserveModes.DEEP;
          break;
      }
    }
    this.internalSetEntry(entry);
    return new Change({ originalValue, currentValue: value });
  }

  protected subscribeEntryValueChangedEvent(entry: ObservableCollectionEntry<K, any>) {
    entry.changedSubscription = (entry.value as ObservableCollection<any, any>).changedEvent
      .subscribe((changedEvent: ChangedEvent) => this.onEntryValueChanged(entry.key, changedEvent));
  }

  protected unsubscribeEntryValueChangedEvent(entry: ObservableCollectionEntry<K, V>) {
    if (entry.changedSubscription) {
      entry.changedSubscription.unsubscribe();
      entry.changedSubscription = null;
    }
  }

  protected onEntryValueChanged(entryKey: K, changedEvent: ChangedEvent) {
    const changes: { [key: string]: Change<V> } = {};
    for (const childKey in changedEvent.changes) {
      if (!changedEvent.changes.hasOwnProperty(childKey)) {
        continue;
      }
      changes[`${entryKey}.${childKey}`] = changedEvent.changes[childKey];
    }
    this.fireChangedEvent(new ChangedEvent({
      source: this,
      changes,
      originalEvent: changedEvent
      // or set only last original event:
      // originalEvent: changedEvent.originalEvent || changedEvent
    }));
  }

  protected onChangedValues(changes: { [key: string]: Change<V> }, options: ChangeOptions): void {
    const silent = options && options.silent || false;
    if (!silent) {
      this.fireChangedEvent(new ChangedEvent({ source: this, changes }));
    }
  }

  protected fireChangedEvent(changedEvent: ChangedEvent) {
    this.changedEventSubject.next(changedEvent);
  }
}

ObjectAdapter.registerAdapterFactory((source: object) => {
  return source instanceof ObservableCollection ? source.toObjectAdapter() : null;
});
